import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { productsAPI } from '../api/vendly';
import NotificationBell from './NotificationBell';
import styles from './Navbar.module.css';

const SEARCH_HISTORY_KEY = 'vendly_search_history';

function getHistory() {
  try { return JSON.parse(localStorage.getItem(SEARCH_HISTORY_KEY) || '[]'); }
  catch { return []; }
}

function addToHistory(term) {
  if (!term.trim()) return;
  const history = getHistory().filter(h => h !== term).slice(0, 9);
  localStorage.setItem(SEARCH_HISTORY_KEY, JSON.stringify([term, ...history]));
}

export default function Navbar() {
  const { count } = useCart();
  const { user, isLoggedIn, logout } = useAuth();
  const [search, setSearch] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [history, setHistory] = useState(getHistory());
  const navigate = useNavigate();
  const searchRef = useRef(null);
  const inputRef = useRef(null);

  // Click outside closes search
  useEffect(() => {
    const handler = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setSearchOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Fetch suggestions from backend
  useEffect(() => {
    if (!search.trim()) { setSuggestions([]); return; }
    const timer = setTimeout(() => {
      productsAPI.list({ q: search, per_page: 5 })
        .then(res => setSuggestions(res.data.products || []))
        .catch(() => setSuggestions([]));
    }, 250);
    return () => clearTimeout(timer);
  }, [search]);

  const openSearch = () => {
    setSearchOpen(true);
    setHistory(getHistory());
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const doSearch = (term) => {
    if (!term.trim()) return;
    addToHistory(term);
    setHistory(getHistory());
    setSearchOpen(false);
    setSearch('');
    navigate(`/shop?q=${encodeURIComponent(term.trim())}`);
  };

  const handleKey = (e) => {
    if (e.key === 'Enter') doSearch(search);
    if (e.key === 'Escape') setSearchOpen(false);
  };

  const handleLogout = () => { logout(); setUserMenuOpen(false); navigate('/'); };

  const filteredHistory = history.filter(h =>
    search ? h.toLowerCase().startsWith(search.toLowerCase()) : true
  ).slice(0, 5);

  return (
    <nav className={styles.nav}>
      <div className={styles.inner}>

        {/* Logo — always visible */}
        <Link to="/home" className={styles.logo}>
          <span className={styles.logoIcon}>V</span>
          {!searchOpen && <span className={styles.logoText}>vendly</span>}
        </Link>

        {/* Search bar */}
        <div ref={searchRef} className={`${styles.searchWrap} ${searchOpen ? styles.searchOpen : ''}`}>
          {searchOpen ? (
            // Expanded search
            <div className={styles.searchExpanded}>
              <div className={styles.searchInputRow}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={styles.searchIcon}>
                  <circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/>
                </svg>
                <input
                  ref={inputRef}
                  className={styles.searchInput}
                  placeholder="Search products…"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  onKeyDown={handleKey}
                />
                <button className={styles.searchCancelBtn} onClick={() => { setSearchOpen(false); setSearch(''); }}>
                  Cancel
                </button>
              </div>

              {/* Dropdown */}
              <div className={styles.searchDropdown}>
                {/* History matches */}
                {filteredHistory.length > 0 && (
                  <div className={styles.dropSection}>
                    <p className={styles.dropLabel}>Recent searches</p>
                    {filteredHistory.map(h => (
                      <button key={h} className={styles.dropItem} onClick={() => doSearch(h)}>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={styles.clockIcon}>
                          <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
                        </svg>
                        <span>{h}</span>
                      </button>
                    ))}
                  </div>
                )}

                {/* Live suggestions */}
                {search.trim() && suggestions.length > 0 && (
                  <div className={styles.dropSection}>
                    <p className={styles.dropLabel}>Suggestions</p>
                    {suggestions.map(p => (
                      <button key={p.id} className={styles.dropItem} onClick={() => doSearch(p.name)}>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={styles.suggestIcon}>
                          <circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/>
                        </svg>
                        <span className={styles.dropItemText}>{p.name}</span>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={styles.arrowIcon}>
                          <line x1="7" y1="17" x2="17" y2="7"/><polyline points="7 7 17 7 17 17"/>
                        </svg>
                      </button>
                    ))}
                  </div>
                )}

                {search.trim() && suggestions.length === 0 && (
                  <div className={styles.dropEmpty}>No results for "{search}"</div>
                )}
              </div>
            </div>
          ) : (
            // Collapsed search pill
            <button className={styles.searchPill} onClick={openSearch}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/>
              </svg>
              <span className={styles.searchPillText}>Search products…</span>
            </button>
          )}
        </div>

        {/* Right actions — hidden when search open on mobile */}
        {!searchOpen && (
          <div className={styles.actions}>
            {/* Cart */}
            {isLoggedIn && <NotificationBell />}
            <Link to="/cart" className={styles.iconBtn} aria-label="Cart">
              <div className={styles.cartIconWrap}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/>
                  <path d="M1 1h4l2.68 13.39a2 2 0 001.99 1.61h9.72a2 2 0 001.99-1.82l1.25-9.35H6"/>
                </svg>
                {count > 0 && <span className={styles.cartBadge}>{count}</span>}
              </div>
            </Link>

            {/* User */}
            {isLoggedIn ? (
              <div className={styles.userWrap}>
                <button className={styles.userBtn} onClick={() => setUserMenuOpen(o => !o)}>
                  <span className={styles.userAvatar}>{user.name.charAt(0).toUpperCase()}</span>
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={styles.chevronDown}>
                    <polyline points="6 9 12 15 18 9"/>
                  </svg>
                </button>
                {userMenuOpen && (
                  <div className={styles.userMenu}>
                    <div className={styles.userMenuHeader}>
                      <p className={styles.userMenuName}>{user.name}</p>
                      <p className={styles.userMenuEmail}>{user.email}</p>
                    </div>
                    <Link to="/orders"  className={styles.userMenuItem} onClick={() => setUserMenuOpen(false)}>📦 My orders</Link>
                    <Link to="/profile" className={styles.userMenuItem} onClick={() => setUserMenuOpen(false)}>👤 Profile settings</Link>
                    <Link to="/support" className={styles.userMenuItem} onClick={() => setUserMenuOpen(false)}>🎧 Contact support</Link>
                    <div className={styles.userMenuDivider} />
                    <button className={styles.userMenuLogout} onClick={handleLogout}>Sign out</button>
                  </div>
                )}
              </div>
            ) : (
              <Link to="/login" className={styles.signinBtn}>Sign in</Link>
            )}
          </div>
        )}
      </div>

      {/* Category bar — desktop only (≥1024px) */}
      <div className={styles.categoryBar}>
        <div className={styles.categories}>
          {[
            { label: 'All', icon: '🛍️' },
            { label: 'Electronics', icon: '📱' },
            { label: 'Fashion', icon: '👗' },
            { label: 'Home & Living', icon: '🏠' },
            { label: 'Beauty', icon: '💄' },
            { label: 'Sports', icon: '⚽' },
            { label: 'Gaming', icon: '🎮' },
            { label: 'Computing', icon: '💻' },
            { label: 'Supermarket', icon: '🛒' },
            { label: 'Foodstuff', icon: '🍔' },
            { label: 'Deals', icon: '🔥' },
          ].map(cat => (
            <button
              key={cat.label}
              className={styles.catLink}
              onClick={() => navigate(`/shop?cat=${cat.label.toLowerCase().replace(/ & /g, '-').replace(/ /g, '-')}`)}
            >
              <span className={styles.catIcon}>{cat.icon}</span>
              {cat.label}
            </button>
          ))}
        </div>
      </div>
    </nav>
  );
}
